import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  BookOpen,
  Building2,
  Clock,
  ScrollText,
  Globe,
  Layers,
  Loader2,
  Pencil,
  LucideIcon,
  RefreshCw,
  Ruler,
  Trash2,
  Upload,
} from "lucide-react";
import GlowCard from "../components/GlowCard";
import KPITile from "../components/KPITile";
import StatusBadge from "../components/StatusBadge";
import ConfirmDialog from "../StandardsLibrary/ConfirmDialog";
import ImportCatalogueModal from "./ImportCatalogueModal";
import AwpRuleSetView, { STATUS_LABEL, STATUS_TONE } from "./AwpRuleSetView";
import { EditDetailsDialog, RuleSetAuditPanel } from "./RuleSetDialogs";
import { AwpRuleSet, RULE_LAYERS, RuleLayer, deleteRuleSet, listRuleSets } from "./rulesApi";

interface AwpRuleLibraryProps {
  /** Reviewer side: browse only — no upload, no status changes, no delete. */
  readOnly?: boolean;
}

const ALL = "All";

const LAYER_ICONS: Record<RuleLayer, LucideIcon> = {
  contract: ScrollText,
  project: BookOpen,
  company: Building2,
  technical: Ruler,
  baseline: Globe,
};

/**
 * The AWP side of Manage Standards — laid out exactly as the engineering library is: the counts, a
 * drop zone, filter chips and one table of standards. Opening a standard shows its rules on a page of
 * their own, the way an engineering standard opens into its workbench.
 *
 * What differs is underneath: an AWP standard is a rule set read from a Word catalogue, sitting at a
 * layer (Contract → Industry Baseline), and arriving as Draft until an administrator activates it.
 */
const AwpRuleLibrary = ({ readOnly = false }: AwpRuleLibraryProps) => {
  const [sets, setSets] = useState<AwpRuleSet[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [layerFilter, setLayerFilter] = useState<RuleLayer | typeof ALL>(ALL);
  const [helpOpen, setHelpOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AwpRuleSet | null>(null);
  const [auditFor, setAuditFor] = useState<AwpRuleSet | null>(null);
  const [editing, setEditing] = useState<AwpRuleSet | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // The open standard lives in the URL as the engineering one does, so a refresh keeps you on it and
  // the sidebar can close it (see NESTED_PARAMS in useNavTab).
  const [params, setParams] = useSearchParams();
  const openId = params.get("standard");
  const setOpenId = useCallback(
    (id: string | null) => {
      const next = new URLSearchParams(params);
      if (id) next.set("standard", id);
      else next.delete("standard");
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const load = useCallback(async () => {
    try {
      setError(null);
      setSets(await listRuleSets());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the standards library.");
      setSets([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const layerLabel = (key: RuleLayer) => RULE_LAYERS.find((l) => l.key === key)?.label ?? key;

  // Highest precedence first, then by title — the order a reviewer would weigh them in.
  const visible = useMemo(
    () =>
      (sets ?? [])
        .filter((s) => layerFilter === ALL || s.layer === layerFilter)
        .sort(
          (a, b) =>
            RULE_LAYERS.findIndex((l) => l.key === a.layer) - RULE_LAYERS.findIndex((l) => l.key === b.layer) ||
            a.title.localeCompare(b.title),
        ),
    [sets, layerFilter],
  );

  const counts = useMemo(() => {
    const out = {} as Record<RuleLayer, number>;
    for (const l of RULE_LAYERS) out[l.key] = (sets ?? []).filter((s) => s.layer === l.key).length;
    return out;
  }, [sets]);

  const openUpload = (file: File | null) => {
    setDroppedFile(file);
    setUploadOpen(true);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) openUpload(file);
  };

  const remove = async (set: AwpRuleSet) => {
    setBusyId(set.id);
    setError(null);
    try {
      await deleteRuleSet(set.id);
      setSets((prev) => (prev ?? []).filter((s) => s.id !== set.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not remove the standard.");
    } finally {
      setBusyId(null);
    }
  };

  const open = (sets ?? []).find((s) => s.id === openId);
  if (open) {
    return (
      <AwpRuleSetView
        set={open}
        readOnly={readOnly}
        onBack={() => setOpenId(null)}
        onChanged={(updated) => setSets((prev) => (prev ?? []).map((s) => (s.id === updated.id ? updated : s)))}
      />
    );
  }

  return (
    <div className="vr-page">
      <div className="vr-page-head">
        <div>
          <h1 className="vr-h-page">
            Standards &amp; Frameworks Library
            <button
              type="button"
              className="vr-info-btn"
              aria-expanded={helpOpen}
              title={helpOpen ? "Hide" : "What this holds"}
              onClick={() => setHelpOpen((o) => !o)}
            >
              i
            </button>
          </h1>
          <p hidden={!helpOpen}>
            The AWP rule sets packages are reviewed against. Each standard sits at a layer, and where two
            disagree the higher layer wins: Contract → Project → Company → Technical Standards → Industry
            Baseline. Standards arrive as Draft and are used once an administrator activates them.
          </p>
        </div>
        {!readOnly && (
          <button className="vr-btn vr-btn-glow" onClick={() => openUpload(null)}>
            <Upload size={15} /> Upload Standard
          </button>
        )}
      </div>

      {/* One tile per layer, highest precedence first — how many standards sit at each. */}
      <div className="vr-grid vr-grid-5">
        {RULE_LAYERS.map((l) => (
          <KPITile key={l.key} icon={LAYER_ICONS[l.key]} label={l.label} value={counts[l.key]} />
        ))}
      </div>

      {!readOnly && (
        <div
          className={`vr-drop ${dragging ? "vr-dragging" : ""}`}
          onClick={() => fileInput.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <Upload size={26} className="vr-drop-icon" />
          <span className="vr-drop-title">Drag and drop a standards document, or click to browse</span>
          <span className="vr-drop-sub">
            PDF, Word or Excel — contracts, project requirements, procedures and technical standards
          </span>
          <input
            ref={fileInput}
            type="file"
            accept=".pdf,.docx,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) openUpload(file);
              e.target.value = "";
            }}
          />
        </div>
      )}

      {error && <div className="vr-form-error">{error}</div>}

      <div className="vr-chips">
        {[{ key: ALL, label: ALL }, ...RULE_LAYERS].map((l) => (
          <button
            key={l.key}
            className={`vr-chip-btn ${layerFilter === l.key ? "vr-active" : ""}`}
            onClick={() => setLayerFilter(l.key as RuleLayer | typeof ALL)}
          >
            {l.label}
          </button>
        ))}
      </div>

      <GlowCard className="vr-table-card" hover={false}>
        <div className="vr-table-head">
          <h3>Governing Standards ({visible.length})</h3>
          <button className="vr-rowbtn" onClick={() => void load()}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>

        <div className="vr-table-scroll">
          <table className="vr-table">
            <thead>
              <tr>
                <th>Standard</th>
                <th>Layer</th>
                <th>Version</th>
                <th>Rules</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((s) => {
                const busy = busyId === s.id;
                return (
                  <tr key={s.id}>
                    <td className="vr-cell-title">
                      <div>
                        <strong>{s.title}</strong>
                        <div className="vr-cell-code">{s.docNo || s.source.fileName}</div>
                      </div>
                    </td>
                    <td className="vr-cell-dim">{layerLabel(s.layer)}</td>
                    <td className="vr-cell-dim">{s.version || "—"}</td>
                    <td className="vr-cell-dim">
                      {s.ruleCount}
                      <div className="vr-cell-code">{s.domains.length} domains</div>
                    </td>
                    <td>
                      <StatusBadge status={STATUS_TONE[s.status]} label={STATUS_LABEL[s.status]} />
                      {s.problems.length > 0 && (
                        <div className="vr-cell-code vr-warn">
                          {s.problems.length} read {s.problems.length === 1 ? "warning" : "warnings"}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="vr-cell-actions">
                        <button
                          className="vr-rowbtn vr-icononly"
                          onClick={() => setOpenId(s.id)}
                          title="Open this standard — its rules"
                          aria-label="Open standard"
                        >
                          <Layers size={13} />
                        </button>
                        {/* The upload-screen details. Locked like everything else once Active. */}
                        {!readOnly && s.status !== "active" && (
                          <button
                            className="vr-rowbtn vr-icononly vr-edit-amber"
                            onClick={() => setEditing(s)}
                            title="Edit — title, document no., version, layer"
                            aria-label="Edit standard"
                          >
                            <Pencil size={13} />
                          </button>
                        )}
                        {/* Who uploaded it and when, activations, unlocks with their reasons, rule edits.
                            Administrators only, and only here on the library list. */}
                        {!readOnly && (
                          <button
                            className="vr-rowbtn vr-icononly"
                            onClick={() => setAuditFor(s)}
                            title="Audit trail — who did what, and when"
                            aria-label="Audit trail"
                          >
                            <Clock size={13} />
                          </button>
                        )}
                        {/* An active standard is locked — it has to be unlocked before it can go. */}
                        {!readOnly && s.status !== "active" && (
                          <button
                            className="vr-rowbtn vr-icononly vr-danger"
                            onClick={() => setConfirmDelete(s)}
                            disabled={busy}
                            title="Remove from the library"
                            aria-label="Remove"
                          >
                            {busy ? <Loader2 size={13} className="vr-spin-inline" /> : <Trash2 size={13} />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {sets === null && (
          <div className="vr-empty">
            <Loader2 size={16} className="vr-spin-inline" /> Loading the library…
          </div>
        )}
        {sets !== null && visible.length === 0 && (
          <div className="vr-empty">
            {sets.length === 0
              ? readOnly
                ? "No AWP standards have been added to the library yet."
                : "No AWP standards yet. Upload one to begin building the library."
              : `No standards at ${layerLabel(layerFilter as RuleLayer)}.`}
          </div>
        )}
      </GlowCard>

      {uploadOpen && (
        <ImportCatalogueModal
          initialFile={droppedFile}
          onCancel={() => {
            setUploadOpen(false);
            setDroppedFile(null);
          }}
          onImported={(set) => {
            setUploadOpen(false);
            setDroppedFile(null);
            setSets((prev) => [...(prev ?? []), set]);
            setOpenId(set.id);
          }}
        />
      )}

      {auditFor && <RuleSetAuditPanel set={auditFor} onClose={() => setAuditFor(null)} />}

      {editing && (
        <EditDetailsDialog
          set={editing}
          onCancel={() => setEditing(null)}
          onSaved={(updated) => {
            setEditing(null);
            setSets((prev) => (prev ?? []).map((s) => (s.id === updated.id ? updated : s)));
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Remove this standard?"
          tone="danger"
          confirmLabel="Remove permanently"
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => {
            const target = confirmDelete;
            setConfirmDelete(null);
            void remove(target);
          }}
        >
          <p className="vr-confirm-lead">
            <strong>{confirmDelete.title}</strong> — {confirmDelete.ruleCount} rules
          </p>
          <p>
            Retiring keeps the rules for the record and stops them being used. Removing deletes them; the
            document would have to be uploaded again.
          </p>
          <p className="vr-confirm-stress">This cannot be undone.</p>
        </ConfirmDialog>
      )}
    </div>
  );
};

export default AwpRuleLibrary;
